import { Quaternion, Vector3 } from "three";
import { Engine } from "../Engine";
import { logger } from "../../util/Logger";

export interface CameraTrackerOptions {
  engine: Engine;
  /** Fires exactly once when real camera motion begins. */
  onMotionStart?: () => void;
  /**
   * Fires on every pose-changing frame while motion is ongoing (after the
   * initial `onMotionStart`). Optional; useful for consumers that need to
   * keep resetting state each tick while moving (e.g. GTAO accumulation).
   */
  onMotionChange?: () => void;
  /** Fires exactly once when motion settles (either via `camera-end` or idle timeout). */
  onMotionEnd?: () => void;
  /**
   * Milliseconds of pose-stability before we declare motion ended if no
   * explicit `camera-end` arrives. Covers wheel zoom and programmatic
   * animations that dispatch `camera-change` without a terminator.
   */
  idleMs?: number;
  /** Label used in logs for easy source identification. */
  label?: string;
}

/**
 * Consolidates raw `camera-*` engine events (`camera-start` / `camera-change` /
 * `camera-end`) into a clean "is the camera actually moving?" signal.
 *
 * Why this exists: `SmoothOrbitController.animate()` re-dispatches
 * `camera-change` every frame unconditionally, so a naive listener can never
 * tell if the camera really moved. The tracker diffs position / quaternion /
 * zoom against the previous tick, filters idle ticks out, and debounces the
 * end-of-motion signal.
 *
 * Emits callbacks instead of engine events to avoid cross-tracker coupling
 * when multiple consumers each own one.
 */
export class CameraTracker {
  private readonly _engine: Engine;
  private readonly _onMotionStart?: () => void;
  private readonly _onMotionChange?: () => void;
  private readonly _onMotionEnd?: () => void;
  private readonly _idleMs: number;
  private readonly _label: string;

  private readonly _lastPos = new Vector3();
  private readonly _lastQuat = new Quaternion();
  private _lastZoom = Number.NaN;

  private _moving = false;
  private _moveStartedAt = 0;
  private _idleTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(opts: CameraTrackerOptions) {
    this._engine = opts.engine;
    this._onMotionStart = opts.onMotionStart;
    this._onMotionChange = opts.onMotionChange;
    this._onMotionEnd = opts.onMotionEnd;
    this._idleMs = opts.idleMs ?? 100;
    this._label = opts.label ?? "CameraTracker";

    this._captureInitialPose();

    this._engine.addEventListener("camera-change", this._onCameraChange);
    this._engine.addEventListener("camera-start", this._onCameraStart);
    this._engine.addEventListener("camera-end", this._onCameraEnd);
  }

  get isMoving(): boolean {
    return this._moving;
  }

  dispose(): void {
    if (this._idleTimer) {
      clearTimeout(this._idleTimer);
      this._idleTimer = null;
    }
    this._engine.removeEventListener("camera-change", this._onCameraChange);
    this._engine.removeEventListener("camera-start", this._onCameraStart);
    this._engine.removeEventListener("camera-end", this._onCameraEnd);
  }

  /**
   * Seed the cached pose from the current active camera so the first
   * `camera-change` tick doesn't falsely report motion just because the
   * cached zoom was still NaN.
   */
  private _captureInitialPose(): void {
    const cam = this._engine.activeCamera?.instance;
    if (!cam) return;
    this._lastPos.copy(cam.position);
    this._lastQuat.copy(cam.quaternion);
    this._lastZoom = (cam as unknown as { zoom?: number }).zoom ?? 1;
  }

  /**
   * Returns true iff the camera's pose drifted since the last check, and
   * updates the cached pose in-place when it did.
   */
  private _consumePoseDelta(): boolean {
    const cam = this._engine.activeCamera?.instance;
    if (!cam) return false;
    const zoom = (cam as unknown as { zoom?: number }).zoom ?? 1;
    const moved =
      !cam.position.equals(this._lastPos) ||
      !cam.quaternion.equals(this._lastQuat) ||
      Math.abs(zoom - this._lastZoom) > 1e-8;
    if (moved) {
      this._lastPos.copy(cam.position);
      this._lastQuat.copy(cam.quaternion);
      this._lastZoom = zoom;
    }
    return moved;
  }

  private _onCameraChange = () => {
    if (!this._consumePoseDelta()) return;
    this._armIdleTimer();
    if (!this._moving) {
      this._beginMotion("camera-change");
    } else {
      this._onMotionChange?.();
    }
  };

  private _onCameraStart = () => {
    this._armIdleTimer();
    if (!this._moving) {
      this._beginMotion("camera-start");
    }
  };

  private _onCameraEnd = () => {
    this._clearIdleTimer();
    if (this._moving) {
      this._endMotion("camera-end");
    }
  };

  private _beginMotion(reason: string): void {
    this._moving = true;
    this._moveStartedAt = performance.now();
    logger.debug("[" + this._label + "] motion-start", reason);
    this._onMotionStart?.();
  }

  private _endMotion(reason: string): void {
    this._moving = false;
    const dt = performance.now() - this._moveStartedAt;
    logger.debug(
      "[" + this._label + "] motion-end",
      reason,
      dt.toFixed(0) + "ms moving",
    );
    this._onMotionEnd?.();
  }

  private _armIdleTimer(): void {
    this._clearIdleTimer();
    this._idleTimer = setTimeout(() => {
      this._idleTimer = null;
      if (this._moving) {
        this._endMotion("idle-timeout");
      }
    }, this._idleMs);
  }

  private _clearIdleTimer(): void {
    if (this._idleTimer) {
      clearTimeout(this._idleTimer);
      this._idleTimer = null;
    }
  }
}
